import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@env/environments';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { NotificationService } from '@core/services/notification.service';
import { PrimengSeverities } from '@core/enums/primeng-severities';
import { RegisterForm } from '@core/model/form/register.model';
import { OrgData, TwoFaResponseModel } from '@core/model/2fe-coode/two-fa-response.model';
import { IDoctorNumberCheck, IDoctorNumberCheckResponse } from '@core/model/lanr/lanr-check.model';
import { RoutingPath } from '../enums/routing-path';
import { Store } from '@ngrx/store';
import { LOAD_USER_DATA } from '@app/components/user-profile/store/user.actions';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  multipleOrganiztionArray: Array<OrgData> = [];
  static readonly USER_ACCESS_TOKEN = 'access_token';
  static readonly USER_ID = 'user_id';
  static readonly ORG_ID = 'orgId';
  private email = '';

  constructor(
    private http: HttpClient,
    private jwtService: JwtHelperService,
    private router: Router,
    private notificationService: NotificationService,
    private store: Store
  ) {
  }

  /**
   * Get the access token from the local storage. Used in the interceptor.
   */
  getAuthToken(): string | null {
    return localStorage.getItem(AuthService.USER_ACCESS_TOKEN);
  }

  /**
   * Get userId
   */
  getOrgId(): string | null {
    return localStorage.getItem(AuthService.ORG_ID);
  }

  /**
   * Login the user
   * @param: { username: string, password: string }
   * @return { accessToken: string }
   */
  loginUser$(authData: { email: string, password: string; }): Observable<TwoFaResponseModel> {
    this.email = authData.email;
    const payload = {
      ...authData,
      platform: 'doctor'
    };
    return this.http.post<TwoFaResponseModel>(`${environment.BASE_URL}/sessions`, payload)
      .pipe(map((data: TwoFaResponseModel): TwoFaResponseModel => {
        if (data.accessToken) {
          this.storeTokenData(data);
          return {
            ...data,
            organizations: this.multipleOrganiztionArray
          };
        }
        if (data.organizations && data.organizations.length > 1) {
          return {
            ...data,
            organizations: data.organizations
          };
        }
        return data;
      }));
  }


  ssoLogin(param: { bsnr: string, lanr: string, sessionid: string, sessionindex: string, selected: boolean }): Observable<TwoFaResponseModel> {
    localStorage.setItem('sId', param.sessionid);
    const payload = {
      bsnr: param.bsnr,
      lanr: param.lanr,
      sessionId: param.sessionid,
      email: null,
      password: null,
      platform: 'doctor',
      ssoLogin: true,
      selected: param.selected
    };
    return this.http.post<TwoFaResponseModel>(`${environment.BASE_URL}/sessions`, payload)
      .pipe(map((data: TwoFaResponseModel): TwoFaResponseModel => {
        if (data.accessToken !== null) {
          this.storeSsoTokenData(data);
          return data;
        }
        if (data.organizations.length > 1) {
          return {
            ...data,
            organizations: data.organizations
          };
        }
        return data;
      }));
  }

  multipleBsnrLogin(id: string, credentials: {email: string, password: string}): Observable<TwoFaResponseModel> {
    const payload = {
      ...credentials,
      bsnr: id,
      selected: true,
      platform: 'doctor'
    }
    return this.http.post<TwoFaResponseModel>(`${environment.BASE_URL}/sessions`, payload)
    .pipe(map((data: TwoFaResponseModel): TwoFaResponseModel => {
      if (data.accessToken) {
        this.storeTokenData(data);
        return {
          ...data,
          organizations: this.multipleOrganiztionArray
        };
      }
      if (data.organizations && data.organizations.length > 1) {
        return {
          ...data,
          organizations: data.organizations
        };
      }
      return data;
    }));
  }

  logoutSsoUser(): Observable<unknown> {
    return this.http.delete(`${environment.BASE_URL}/sessions`)
      .pipe();
  }

  /**
   * Register the user
   * @param: {user : IRegisterForm }
   */
  registerUser$(user: RegisterForm): Observable<unknown> {
    return this.http.post<Observable<unknown>>(`${environment.BASE_URL}/users/register`, user)
      .pipe(catchError((error: HttpErrorResponse): Observable<unknown> => {
        this.handleError(error);
        return of();
      }));
  }

  private handleError(errorRes: HttpErrorResponse): void {
    if (errorRes.message) {
      const validationMessages = JSON.parse(errorRes.error.message);
      if (validationMessages.email) {
        if (validationMessages.email.includes('validation.unique')) {
          this.notificationService.show(PrimengSeverities.Error, 'register.email_already_used');
        }
      }
      else {
        this.notificationService.show(PrimengSeverities.Error, 'register.user_creation_error');
      }
    }
  }

  /**
   * Send two-way  authentication code.
   */
  sendCodeSMS(): void {
    const payload = {
      email: this.email
    };
    this.http.post(`${environment.BASE_URL}/sessions/2fa`, payload)
      .subscribe();
  }

  /**
   * Logout the user
   */

  logout(): Observable<any | unknown> {
    if (localStorage.getItem('isSso') === '1') {
      return this.http.delete(`${environment.BASE_URL}/sessions`).pipe(map((response: any): void => {
        window.location.href = `${'https://check-arzt.essb.online/'}`;
        localStorage.clear();
        return response;
      }));
    }
    localStorage.clear();
    this.router.navigate([RoutingPath.Login]).then();
    return of();
  }

  /**
   * Authenticate with th code received by SMS/Mail.
   * @param: { code: string }
   * @return { accessToken: string }
   */
  authenticateWithCode(code: string): Observable<TwoFaResponseModel> {
    const payload = {
      email: this.email,
      '2faCode': code,
      platform: 'doctor',
      bsnr: localStorage.getItem('orgNumber'),
      selected: true
    };
    return this.http.post<TwoFaResponseModel>(`${environment.BASE_URL}/sessions/2fa`, payload)
      .pipe(map((data: TwoFaResponseModel): TwoFaResponseModel => {
        if (data.accessToken) {
          this.storeTokenData(data);
          return {
            ...data,
            organizations: this.multipleOrganiztionArray
          };
        }
        return data;
      }));
  }

  /**
   * Search Doctor based on LANR Name Surname and Org number
   * @param payload {{ IDoctorNumberCheck }}
   */
  checkIdentificationNumber$(payload: IDoctorNumberCheck): Observable<IDoctorNumberCheckResponse> {
    return this.http.post<IDoctorNumberCheckResponse>(`${environment.BASE_URL}/users/lanr`, payload)
      .pipe(map((res: IDoctorNumberCheckResponse): IDoctorNumberCheckResponse => {
        return res;
      }));
  }

  /**
   * @type 'private'
   * Store the Token Data
   * @param: { data: { accessToken: string } }
   */

  private storeSsoTokenData = (data: TwoFaResponseModel): void => {
    localStorage.clear();
    if (!data.accessToken) {
      return;
    }
    const decodeToken = this.jwtService.decodeToken(data.accessToken);
    localStorage.setItem('orgId', decodeToken.data.organizations[0].id);

    localStorage.setItem(AuthService.USER_ACCESS_TOKEN, data.accessToken);
    localStorage.setItem(AuthService.USER_ID, decodeToken.data.id);
    this.store.dispatch(LOAD_USER_DATA());
  };

  private storeTokenData = (data: TwoFaResponseModel): void => {
    localStorage.clear();
    if (!data.accessToken) {
      return;
    }
    const decodeToken = this.jwtService.decodeToken(data.accessToken);
    this.multipleOrganiztionArray = decodeToken.data.organizations;
    localStorage.setItem(AuthService.USER_ACCESS_TOKEN, data.accessToken);
    localStorage.setItem(AuthService.USER_ID, decodeToken.data.id);
    this.store.dispatch(LOAD_USER_DATA());
  };

}
